import React, { Fragment, memo, useEffect, useState } from "react";
import GeneralSetupTab from "../../components/GeneralSetupTab";
import CenterWrapper from "../../components/CenterWrapper";
import { ArrowUpRightIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { fixUnixTime } from "../../utils/dates";
import MasterCardPNG from "../../imgs/mastercard.png";
import VisaPNG from "../../imgs/visa.png";
import { capitalize } from "lodash";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PlanPaymentResultModal from "../../components/PlanPaymentResultModal";
import { useRecoilValue } from "recoil";
import { billingAtom } from "../../store/billingAtom";
import useCompanyUsage from "../../hooks/useCompanyUsage";
import useInvoices from "../../hooks/useInvoices";
import usePaymentMethod from "../../hooks/usePaymentMethod";
import SecondaryButton from "../../components/SecondaryButton";
import GlobalLoadingSpinner from "../../components/GlobalSpinner";

const ManageBilling = () => {
  useCompanyUsage();
  const { invoices, loading: invLoading } = useInvoices();
  const { data: paymentMethod } = usePaymentMethod();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const action = searchParams.get("action");
  const billingInfo = useRecoilValue(billingAtom);

  const [showPaymentPlanResultModal, setShowPaymentPlanResultModal] =
    useState(false);

  const handleCardImage = brand => {
    return brand === "visa" ? VisaPNG : MasterCardPNG;
  };

  const active_plan = billingInfo?.active_plan?.payment_plan;
  const next_plan = billingInfo?.next_plan?.payment_plan;

  const getCost = () => {
    const cost =
      active_plan?.type === "annually"
        ? +active_plan?.cost / 12
        : +active_plan?.cost;
    return cost ? cost.toFixed(2).replace(/\.00$/, "") : 0;
  };

  const cost = getCost();

  const getDescription = () => {
    if (
      +next_plan?.cost > 0 &&
      +next_plan?.cost < +active_plan?.cost
    ) {
      return `Downgraded to ${next_plan?.name || "N/A"} plan, current ${active_plan?.name || "N/A"} subscription ends on ${billingInfo?.next_renewal || "N/A"}`;
    }

    if (
      +next_plan?.cost === 0 &&
      +next_plan?.cost < +active_plan?.cost
    ) {
      return `Canceled ends on ${billingInfo?.next_renewal || "N/A"}`;
    }

    return `Renews on ${billingInfo?.next_renewal || "N/A"} - Messages do not roll over.`;
  };

  const description = getDescription();

  useEffect(() => {
    if (action) {
      setShowPaymentPlanResultModal(true);
    }
  }, [action]);

  return (
    <>
      <CenterWrapper>
        <div className="" style={{ paddingBottom: "10px" }}>
          <GeneralSetupTab
            title={"Account Settings"}
            subtitle={"Update your company settings, team and billing."}
            pageType={"settings"}
            activeTab={"manage_billing"}
          />

          <div className="max-w-6xl m-auto w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="flex flex-col justify-between border border-gray-200 rounded-xl bg-white">
                <div className="rounded-lg p-6">
                  <div className="flex flex-col justify-between mt-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 p-0 m-0">
                        <span className="mr-2">
                          {active_plan?.name || "Free plan"}
                        </span>
                        <span className="text-sm text-primary bg-primary/10 px-2 py-1 rounded-full font-medium">
                          {active_plan?.type === "annually"
                            ? "Annual"
                            : "Monthly"}{" "}
                          billing
                        </span>
                      </h3>

                      <div className="flex items-end">
                        <span className="text-4xl font-bold text-gray-900">
                          ${cost}
                        </span>
                        <span className="text-gray-600 text-md">per month</span>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm p-0 m-0">
                      {description}
                    </p>
                  </div>

                  <div className="mt-4">
                    <p className="text-sm text-gray-600 font-semibold">
                      {billingInfo?.credits_consumed || 0}/
                      {billingInfo?.subscription_limit || 100} messages used
                    </p>
                    <div className="w-full bg-gray-200 h-2 rounded-full mt-1">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{
                          width: `${Math.min(
                            (billingInfo?.credits_consumed /
                              billingInfo?.subscription_limit) *
                              100,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="p-4 border-t mt-2 flex justify-end">
                  <Link
                    className="flex items-center text-sm text-gray-500"
                    to="/settings/plans"
                  >
                    <span className="font-semibold text-primary mr-2">
                      Upgrade Plan
                    </span>
                    <ArrowUpRightIcon
                      aria-hidden="true"
                      className="size-4 text-primary"
                    />
                  </Link>
                </div>
              </div>

              <div className="border rounded-lg p-6 ">
                <h3 className="text-lg font-semibold ">Payment method</h3>
                <p className="text-gray-600 text-sm">
                  Change how you pay for your plan.
                </p>
                <div className="mt-3 flex items-start border p-4 rounded-lg bg-white justify-between gap-3">
                  {paymentMethod ? (
                    <div className="flex items-start gap-3">
                      <div className="rounded-xl p-4 border">
                        <img
                          src={`${handleCardImage(paymentMethod.brand)}`}
                          alt={`${paymentMethod.brand}`}
                          className="h-4"
                        />
                      </div>

                      <div>
                        <p className="text-gray-800 text-sm font-medium p-0 m-0">
                          {capitalize(paymentMethod.brand)} ending in{" "}
                          {paymentMethod.last4}
                        </p>
                        <p className="text-gray-600 text-xs p-0 m-0">
                          Expiry {paymentMethod.exp_year}
                        </p>
                        <div className="text-gray-600 flex items-center gap-1 mt-2">
                          <EnvelopeIcon className="size-4" />
                          <p className="text-xs p-0 m-0">
                            {paymentMethod.billing_email}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-800 text-sm font-medium p-0 m-0">
                      No payment details
                    </p>
                  )}

                  <Link to="https://stripe.com/billing" target="_blank">
                    <SecondaryButton type="button">Edit</SecondaryButton>
                  </Link>
                </div>
              </div>
            </div>

            <h2 className="text-lg font-bold mb-1">Billing and invoicing</h2>
            <p className="text-sm text-gray-600 mb-4">Review your bills</p>

            <div className="mx-4 mt-10 ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg relative overflow-hidden">
              {invLoading && <GlobalLoadingSpinner />}
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Invoice
                    </th>
                    <th
                      scope="col"
                      className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    >
                      Billing Date
                    </th>
                    <th
                      scope="col"
                      className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    >
                      Description
                    </th>

                    <th
                      scope="col"
                      className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(invoice => (
                    <tr
                      className="even:bg-gray-50 border-t hover:bg-gray-50 text-sm"
                      key={invoice}
                    >
                      <td className="py-3 px-4 flex items-center">
                        <span className="bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
                          PDF
                        </span>
                        <span className="ml-3 text-gray-700">
                          {invoice.number}
                        </span>
                      </td>
                      <td className="hidden px-3 py-3.5 text-sm text-gray-500 sm:table-cell">
                        <p className="font-medium text-gray-900">
                          {fixUnixTime(invoice.created)}
                        </p>
                      </td>
                      <td className="hidden px-3 py-3.5 text-sm text-gray-500 sm:table-cell">
                        <p className="font-medium text-gray-800">
                          <span className="px-3 py-1 text-sm font-medium bg-green-100 text-green-700 rounded-full">
                            Paid
                          </span>
                        </p>
                      </td>
                      <td className="hidden px-3 py-3.5 text-sm text-gray-500 sm:table-cell">
                        <p className="font-medium text-gray-900">
                          ${invoice.amount_due}
                        </p>
                      </td>
                      <td className="hidden px-3 py-3.5 text-sm text-gray-500 sm:table-cell">
                        <p className="font-medium text-gray-900">
                          {invoice.description}
                        </p>
                      </td>
                      <td className="py-3 px-4 flex items-center">
                        <a
                          href={invoice.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white text-black text-xs font-semibold px-1 py-1 rounded"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="size-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                            />
                          </svg>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CenterWrapper>

      <PlanPaymentResultModal
        action={action}
        open={showPaymentPlanResultModal}
        onClose={() => {
          setShowPaymentPlanResultModal(false);
          setTimeout(() => {
            navigate(location.pathname);
          }, 500);
        }}
      />
    </>
  );
};

export default memo(ManageBilling);
